import { TextareaHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { useAccessibility } from '../contexts/AccessibilityContext'

interface SeniorInputProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label: string
  helpText?: string
  error?: string
  icon?: ReactNode
  type?: 'textarea'
}

interface SeniorTextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string
  helpText?: string
  error?: string
  icon?: ReactNode
  type?: 'text' | 'email' | 'password'
}

type SeniorInputCombinedProps = SeniorInputProps | SeniorTextInputProps

export const SeniorInput = ({ 
  label,
  helpText,
  error,
  icon,
  type = 'textarea',
  className = '',
  ...props 
}: SeniorInputCombinedProps) => {
  const { settings } = useAccessibility()

  const getLabelClasses = () => {
    const baseSize = settings.fontSize === 'extra-large' ? 'text-2xl' : 
                     settings.fontSize === 'large' ? 'text-xl' : 
                     settings.fontSize === 'medium' ? 'text-lg' : 'text-base'
    
    const contrast = settings.contrast === 'high' ? 'text-black' : 'text-gray-700'
    
    return `${baseSize} ${contrast} font-semibold mb-3 flex items-center gap-2`
  }

  const getInputClasses = () => {
    const baseSize = settings.fontSize === 'extra-large' ? 'text-xl' : 
                     settings.fontSize === 'large' ? 'text-lg' : 
                     settings.fontSize === 'medium' ? 'text-base' : 'text-sm'
    
    const contrast = settings.contrast === 'high' 
      ? 'border-black focus:border-black focus:ring-black' 
      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
    
    const errorStyle = error 
      ? 'border-red-500 focus:border-red-500 focus:ring-red-200' 
      : contrast

    return `
      w-full
      ${baseSize}
      p-4
      ${type === 'textarea' ? 'min-h-32' : 'min-h-14'}
      border-2
      ${errorStyle}
      rounded-lg
      focus:ring-4
      focus:outline-none
      transition-colors
      resize-none
      ${className}
    `
  }

  const getHelpTextClasses = () => {
    const baseSize = settings.fontSize === 'extra-large' ? 'text-lg' : 
                     settings.fontSize === 'large' ? 'text-base' : 
                     settings.fontSize === 'medium' ? 'text-sm' : 'text-xs'
    
    const color = error ? 'text-red-600' : 
                  settings.contrast === 'high' ? 'text-gray-800' : 'text-gray-600'
    
    return `${baseSize} ${color} mt-2`
  }

  const inputId = props.id || `input-${Math.random().toString(36).substr(2, 9)}`

  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className={getLabelClasses()}>
        {icon && <span>{icon}</span>}
        {label}
      </label>
      
      {type === 'textarea' ? (
        <textarea
          id={inputId}
          className={getInputClasses()}
          aria-describedby={helpText || error ? `${inputId}-help` : undefined}
          aria-invalid={error ? 'true' : 'false'}
          {...props as TextareaHTMLAttributes<HTMLTextAreaElement>}
        />
      ) : (
        <input
          id={inputId}
          type={type}
          className={getInputClasses()}
          aria-describedby={helpText || error ? `${inputId}-help` : undefined}
          aria-invalid={error ? 'true' : 'false'}
          {...props as InputHTMLAttributes<HTMLInputElement>}
        />
      )}
      
      {(helpText || error) && (
        <div id={`${inputId}-help`} className={getHelpTextClasses()}>
          {error || helpText}
        </div>
      )}
    </div>
  )
}